<script setup lang="ts">
const { locale, t } = useI18n()

const query = ref('')

const searchQuery = computed(() => {
  if (!query.value?.trim()?.length) {
    return null
  }
  return query.value
})

const { data, error, execute, refresh, clear, pending } = await useAsyncData(
  () => `search-${locale.value}-${searchQuery.value}`,
  () => $fetch('/api/payload-gql', {
    method: 'POST',
    body: {
      query: ManySearchQuery,
      variables: {
        locale: locale.value,
        query: searchQuery.value,
        now: new Date().toISOString(),
      },
      skipCache: true,
    },
  }),
  { immediate: false }
)

watch(searchQuery, () => {
  if (searchQuery.value && searchQuery.value?.split('')?.length >= 2) {
    execute()
  } else {
    clear()
  }
})

watch(locale, () => {
  if (searchQuery.value && searchQuery.value?.split('')?.length >= 2) {
    refresh()
  } else {
    clear()
  }
})

// groups results by collection; the site's own pages and global pages
// (e.g. the news/programme/people/rooms landing pages) share a group since
// they're both "pages" from a visitor's perspective
const groupLabels: Record<string, string> = {
  pages: 'search.groups.pages',
  news: 'search.groups.news',
  programme: 'search.groups.programme',
  people: 'search.groups.people',
  rooms: 'search.groups.rooms',
  global_pages: 'search.groups.pages',
}

const getGroupKey = (relationTo: string) => {
  return relationTo === 'pages' || relationTo === 'global-pages' ? 'pages' : relationTo
}

// unreachable people are already kept out of the index itself (payload's
// `search-indexable.ts`); this stays as a cheap guard for the window between a
// person becoming unreachable and their record being re-synced
const linkablePeopleSlugs = useLinkablePeopleSlugs()

const isReachable = (doc: any) => {
  if (doc?.relationTo !== 'people') return true
  return !!doc?.value?.slug && linkablePeopleSlugs.value.has(doc.value.slug)
}

const resultsGrouped = computed(() => {
  if (!data.value?.docs?.length) {
    return []
  }

  // doc.value can come back null for a search entry whose target no longer
  // resolves (e.g. deleted or filtered out by the visibleUntil/content
  // where-clause but not yet re-indexed) -- drop those before grouping so a
  // group only exists, and only shows a label, when it has real items left.
  const docsWithGroupKey = data.value.docs
    .filter((doc) => doc?.doc?.relationTo && doc?.doc?.value && isReachable(doc.doc))
    .map((doc) => ({
      ...doc,
      doc: {
        ...doc.doc,
        groupKey: getGroupKey(doc.doc.relationTo),
      }
    }))

  const groups = docsWithGroupKey.reduce((acc, item) => {
    const group = acc.find((g) => g.groupKey === item.doc.groupKey)
    if (group) {
      group.items.push(item)
    } else {
      const groupKey = item.doc.groupKey

      acc.push({
        relationTo: item.doc.relationTo,
        title: t(groupLabels[groupKey] ?? groupKey),
        items: [item],
        groupKey,
      })
    }
    return acc
  }, [] as { relationTo: string, items: typeof data.value.docs, groupKey: string, title: string }[])

  const groupSortedByKey = groups.sort((a, b) => a.title.localeCompare(b.title))

  return groupSortedByKey
})

const resultsCount = computed(() => {
  return resultsGrouped.value.reduce((sum, group) => sum + group.items.length, 0)
})

const noSearchResults = computed(() => {
  return resultsCount.value === 0 && searchQuery.value?.split('')?.length >= 2 && !pending.value
})

// 2
const showSearch = useState('showSearch')

useEscapeLayer(showSearch, () => {
  showSearch.value = false
})

// 3
const contentTarget = ref<HTMLElement | null>(null)
const inputTarget = ref<HTMLInputElement | null>(null)

onClickOutside(contentTarget, () => {
  if (showSearch.value) {
    showSearch.value = false
  }
}, { ignore: ['button'] })

watch(showSearch, (newVal) => {
  if (newVal) {
    // FocusScope already moves focus into the panel, this makes sure it lands
    // on the input rather than the first result link
    setTimeout(() => {
      inputTarget.value?.focus()
    }, 100)
  } else {
    query.value = ''
    clear()
  }
})

const getLinkData = (item: any) => {
  return {
    linkType: 'internalLink',
    internalLink: {
      relationTo: item.doc.relationTo,
      value: item.doc.value,
    },
  }
}
</script>

<template>
  <transition name="overlay-fade-noblur">
    <div
      v-if="showSearch"
      class="fixed inset-0 z-search-mask bg-black/05 backdrop-grayscale-05"
    />
  </transition>

  <transition name="overlay-fade-noblur">
    <FocusScope
      v-if="showSearch"
      as="div"
      class="fixed top-0 right-0 z-search overflow-scroll max-h-screen container-d bg-white shadow-[0_0_1em_rgba(0,0,0,0.05)]"
      loop
      trapped
    >
      <div
        class="pt-1 pb-1 px-1"
        ref="contentTarget"
      >
        <div class="flex gap-1">
          <input
            ref="inputTarget"
            v-model="query"
            type="text"
            :placeholder="$t('search.placeholder')"
            class="w-full bg-white text-black placeholder:text-gray !text-md focus-visible:outline-none b-none"
          />

          <button
            @click="showSearch = false"
            class="group ml-auto text-md w-[var(--lh)] h-[var(--lh)] overflow-visible relative"
          >
            <svgo-cross class="absolute inset-0 stroke-default stroke-current w-full overflow-visible group-hover:stroke-accent group-focus-visible:stroke-accent icon-hover" />
          </button>
        </div>

        <div
          v-if="resultsGrouped?.length || noSearchResults"
        >
          <div
            v-if="resultsGrouped?.length"
          >
            {{ $t('search.resultsFor', { count: resultsCount, query: searchQuery }) }}
          </div>

          <div
            v-if="resultsGrouped?.length"
            class="mt-1"
          >
            <ul
              v-for="(group, key) in resultsGrouped"
              :key="key"
              class="not-last:mb-1"
            >
              <li
                class="text-gray "
              >
                {{ group.title }}
              </li>

              <li
                v-for="item in group.items"
                :key="item.id"
                class="flex"
              >
                <site-payload-link
                  :data="getLinkData(item)"
                  class="text-sm flex flex-wrap gap-1 text-hover"
                  @click.up="showSearch = false"
                >
                  {{ item.title }}
                </site-payload-link>
              </li>
            </ul>
          </div>

          <div
            v-if="noSearchResults"
          >
            {{ $t('search.noResults', { query: query }) }}
          </div>
        </div>
      </div>
    </FocusScope>
  </transition>
</template>
